import React, { ChangeEventHandler, useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse } from "qs";

import { Category } from "@/constants/events";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getIndicatedAddress } from "@/redux/selectors/location";
import { leaflyShopStringify } from "@/utils/qs";

import SearchInput from "@/components/SearchInput";
import TrackImpression from "@/components/TrackImpression";

import { FULFILLMENT_LABEL_MAP } from "../FulfillmentFilters/FulfillmentFilters";
import { extractFilterValues } from "../utils/filterUtils";

export const createHeaderString = (
  selectedCategories: string[] | null,
  selectedFulfillments: string[] | null,
  indicatedAddress: string | null,
) => {
  const assembledHeader = [];

  // Category portion of header string
  if (selectedCategories) {
    assembledHeader.push(
      `Best ${selectedCategories.map((c) => c.toLowerCase()).join(" and ")}`,
    );
  } else {
    assembledHeader.push("Shop cannabis products");
  }

  // Fulfillment portion of header string
  if (selectedFulfillments) {
    assembledHeader.push(
      `for ${selectedFulfillments
        .map((c) => FULFILLMENT_LABEL_MAP[c].toLowerCase())
        .join(" or ")}`,
    );
  }

  // Location portion of header string
  if (indicatedAddress) {
    assembledHeader.push(`near ${indicatedAddress}`);
  }

  return assembledHeader.join(" ");
};

const Header: React.FC = () => {
  const { publishEvent } = useEventTracker();
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const parsedQs = parse(searchParams?.toString() || "");
  const indicatedAddress = useSelector(getIndicatedAddress);
  const { q: _q, page: _page, ...other } = parsedQs;
  const query = parsedQs.q as string;
  const { selectedCategories, selectedFulfillments } =
    extractFilterValues(parsedQs);

  const attemptedRetailerName = parsedQs.attemptedRetailerName;

  const setQuery = (value?: string) => {
    const newQueryString = leaflyShopStringify({
      ...other,
      q: value,
    });
    push(`${pathname}${newQueryString ? `?${newQueryString}` : ""}`);
  };

  // * Local component state for input
  const [inputValue, setInputValue] = useState(query || "");
  // * Used to track the initial q parameter on page load
  const [initialSearch, _] = useState(query);

  const triggerSearch = (value: string) => {
    publishEvent({
      action: "click",
      category: Category.unifiedShoppingExperience,
      label: "search bar",
      // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
      searchedFor: value,
    });
    setQuery(value);
  };

  const changeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    setInputValue(e.target.value);
  };

  useEffect(() => {
    // * Sync local input state with quey param, if the query param
    // * is updated elsewhere.
    setInputValue(query || "");
  }, [query]);

  const headerString = attemptedRetailerName
    ? `No results for ${attemptedRetailerName}. Shop from these nearby retailers near ${indicatedAddress}`
    : createHeaderString(
        selectedCategories,
        selectedFulfillments,
        indicatedAddress,
      );

  return (
    <div className="mb-xl">
      <div className="container mt-xl">
        <TrackImpression
          category={Category.unifiedShoppingExperience}
          label="search button"
          customDimensions={{
            default_option: initialSearch,
          }}
        >
          <SearchInput
            inputProps={{
              className: "h-12",
              onChange: changeHandler,
              onKeyDown: (e) => {
                if (e.key && e.key !== "Enter") {
                  return;
                }

                triggerSearch(inputValue);
              },
              placeholder: "Search products",
              value: inputValue,
            }}
            clearButtonProps={{
              onClick: () => setQuery(),
            }}
            searchButtonProps={{
              onClick: () => triggerSearch(inputValue),
            }}
          />
        </TrackImpression>
        <h1 className="mt-lg text-md">{headerString}</h1>
      </div>
    </div>
  );
};

export default Header;
